// Mapa coroplético de España por provincia (d3-geo + SVG, sin dependencias de
// React). Colorea cada provincia según el valor (nº operaciones o importe) de
// un año. GeoJSON servido desde /geo/spain-provinces.geojson.
import React, { useEffect, useState } from "react";
import { geoMercator, geoPath } from "d3-geo";

let _geoCache = null; // cache por sesión para no re-descargar/parsear

const simplify = (s) =>
  (s || "").toString().toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9 ]/g, " ").replace(/\s+/g, " ").trim();

// Alias de nuestros nombres de provincia → token simplificado del GeoJSON
const PROV_ALIAS = {
  "ca madrid": "madrid", "coruna": "a coruna", "la coruna": "a coruna",
  "alava": "araba alava", "araba": "araba alava",
  "vizcaya": "bizkaia vizcaya", "bizkaia": "bizkaia vizcaya",
  "guipuzcoa": "gipuzkoa guipuzcoa", "gipuzkoa": "gipuzkoa guipuzcoa",
  "alicante": "alacant alicante", "alacant": "alacant alicante",
  "castellon": "castello castellon", "castello": "castello castellon",
  "valencia": "valencia valencia",
  "baleares": "illes balears", "mallorca": "illes balears",
  "tenerife": "santa cruz de tenerife",
};
const dataKey = (name) => { const s = simplify(name); return PROV_ALIAS[s] || s; };

export default function SpainProvinceMap({ valueByProvince, format, metricLabel, maxValue, onProvinceClick }) {
  const [geo, setGeo] = useState(_geoCache);
  const [hover, setHover] = useState(null);
  const W = 540, H = 380;

  useEffect(() => {
    if (_geoCache) { setGeo(_geoCache); return; }
    fetch(`${process.env.PUBLIC_URL || ""}/geo/spain-provinces.geojson`)
      .then((r) => r.json())
      .then((g) => { _geoCache = g; setGeo(g); })
      .catch(() => {});
  }, []);

  if (!geo) {
    return <div className="text-xs text-gray-400 py-10 text-center" data-testid="spain-map-loading">Cargando mapa…</div>;
  }

  const projection = geoMercator().fitSize([W, H], geo);
  const path = geoPath().projection(projection);

  const byKey = {};
  Object.entries(valueByProvince || {}).forEach(([name, v]) => {
    const k = dataKey(name);
    byKey[k] = (byKey[k] || 0) + (v || 0);
  });
  const max = maxValue != null ? Math.max(1, maxValue) : Math.max(1, ...Object.values(byKey));

  const colorFor = (v) => {
    if (!v) return "#eef2f7";
    const t = Math.min(1, v / max);
    const lerp = (a, b) => Math.round(a + (b - a) * t);
    return `rgb(${lerp(209, 0)},${lerp(231, 121)},${lerp(221, 52)})`;
  };
  const fmt = (v) => (format ? format(v) : v.toLocaleString("es-ES"));

  return (
    <div className="relative" data-testid="spain-province-map">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" role="img" aria-label="Mapa de España por provincia">
        {geo.features.map((f, i) => {
          const name = f.properties.name;
          const v = byKey[simplify(name)] || 0;
          return (
            <path
              key={i} d={path(f)} fill={colorFor(v)} stroke="#ffffff" strokeWidth={0.4}
              onMouseEnter={() => setHover({ name, v })} onMouseLeave={() => setHover(null)}
              onClick={onProvinceClick ? () => onProvinceClick(name) : undefined}
              data-testid={`map-prov-${simplify(name).replace(/ /g, "-")}`}
              style={{ transition: "opacity .15s" }}
              className={`hover:opacity-75 ${onProvinceClick ? "cursor-pointer" : "cursor-default"}`}
            >
              <title>{name}: {fmt(v)}</title>
            </path>
          );
        })}
      </svg>
      <div className="absolute top-1 left-1 bg-white/90 border border-gray-200 rounded px-2 py-1 text-[11px] text-gray-700 shadow-sm" data-testid="spain-map-hover">
        {hover ? <><b>{hover.name}</b>: {fmt(hover.v)}</> : <span className="text-gray-400">Pasa el ratón por una provincia</span>}
      </div>
      <div className="flex items-center gap-2 mt-1 text-[10px] text-gray-500">
        <span>0</span>
        <div className="h-2 w-28 rounded" style={{ background: "linear-gradient(to right,#d1e7dd,#007934)" }} />
        <span>{fmt(max)} máx · {metricLabel}</span>
      </div>
    </div>
  );
}
